<script setup lang="ts">
/**
 * Singapura Verification — 반복부(인물/감사인) 블록. 원본 서식의 라벨|값 표를 그대로 재현하고
 *   값칸에 CddVarField. Add/Remove 로 개수 조절. 저장 = fill 키 `${prefix}_${i}_${field.s}` + `${countKey}`.
 */
import { computed } from 'vue';
import CddVarField from '../../aura/agm/cdd/CddVarField.vue';
import { useAgmFill } from '../../aura/agm/cdd/useCddFill';
import type { VLoop } from './verifyLoops';

const props = defineProps<{ loop: VLoop }>();
const fill = useAgmFill();

const count = computed(() => {
  const n = parseInt(String(fill.values[props.loop.countKey] ?? '').trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
});
const items = computed(() => Array.from({ length: count.value }, (_, i) => i + 1));
function keyFor(i: number, s: string) { return `${props.loop.prefix}_${i}_${s}`; }
function letter(i: number) { return String.fromCharCode(96 + i); }
function addItem() { fill.setValue(props.loop.countKey, String(count.value + 1)); }
function removeItem(idx: number) {
  const n = count.value;
  for (let k = idx; k < n; k++) {
    for (const f of props.loop.fields) fill.setValue(keyFor(k, f.s), String(fill.values[keyFor(k + 1, f.s)] ?? ''));
  }
  for (const f of props.loop.fields) fill.setValue(keyFor(n, f.s), '');
  fill.setValue(props.loop.countKey, String(Math.max(0, n - 1)));
}
</script>

<template>
  <div class="vlb">
    <div class="sec-title vlb-t">
      {{ loop.title }}:
      <button type="button" class="vlb-add" @click="addItem">+ Add</button>
    </div>

    <p v-if="!count" class="doc-note">— none — (click “+ Add” to enter)</p>

    <table v-for="i in items" :key="i" class="cdd-tbl vlb-tbl">
      <tbody>
        <tr>
          <td class="cdd-lcell vlb-hd" :colspan="2">
            <span>{{ loop.letter ? `(${letter(i)})` : `#${i}` }}</span>
            <button type="button" class="vlb-rm" title="Remove" @click="removeItem(i)">× remove</button>
          </td>
        </tr>
        <tr v-for="f in loop.fields" :key="f.s">
          <td class="cdd-lcell">{{ f.label }}</td>
          <td><CddVarField :field-key="keyFor(i, f.s)" :label="f.label" /></td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped>
.vlb { margin-top: 10px; }
.vlb-t { display: flex; align-items: center; gap: 10px; }
.vlb-add { border: 1px solid #cfe0ff; background: #f4f8ff; color: #1B64DA; font-size: 11px; font-weight: 800; border-radius: 7px; padding: 2px 10px; cursor: pointer; }
.vlb-add:hover { background: #e6f0ff; }
.vlb-tbl { width: auto; margin-top: 8px; }
.vlb-hd { background: #f2f6fc; font-weight: 700; }
.vlb-hd span { color: #2f6bd6; }
.vlb-rm { float: right; border: none; background: none; color: #c0392b; font-size: 11px; cursor: pointer; }
</style>
